import React, { useEffect, useRef, useState, useCallback } from "react";
import html2pdf from "html2pdf.js";
import { useSelector } from "react-redux";
import { mgt2Api } from "../registerFormApi/Mgt1";
import DebentureHoldingSummary from "./DebentureHoldingSummary";

/* ═══════════════════════════════════════════════
   READABLE PDF STYLES - Portrait A4, proper sizing
   ═══════════════════════════════════════════════ */
const PAGE = {
  width: "210mm",
  minHeight: "297mm",
  padding: "8mm 6mm",
  fontFamily: "'Arial', sans-serif",
  fontSize: "8px",
  color: "#000",
  boxSizing: "border-box",
  background: "#fff",
};

const LABEL_CELL = {
  width: "180px",
  verticalAlign: "top",
  paddingRight: "4px",
  fontSize: "7px",
};

const VALUE_CELL = {
  verticalAlign: "top",
  paddingLeft: "2px",
  fontSize: "7px",
};

const TH = {
  border: "1px solid #666",
  paddingLeft: "6px",
  paddingBottom: "6px",
  textAlign: "center",
  verticalAlign: "middle",
  fontWeight: "bold",
  fontSize: "8px",
  /* 🔥 THIS IS THE KEY */
  whiteSpace: "normal", // allow line breaks
  wordBreak: "break-word", // break long words
  overflowWrap: "break-word",
};

const TD = {
  border: "1px solid #666",
  paddingLeft: "6px",
  textAlign: "left",
  paddingBottom: "6px",
  // verticalAlign: "top",
  fontSize: "8px",

  // whiteSpace: "normal",
  // wordBreak: "break-word"
};

const emptyForm = () => ({
  allotmentOrTransferNo: "",
  dateOfAllotmentOrTransfer: "",

  numberOfDebenturesOrSecuritiesAllottedOrTransferred: "",

  distinctiveNumbersFrom: "",
  distinctiveNumbersTo: "",

  folioOfTransferor: "",
  nameOfTransferor: "",

  dateOfIssueOrEndorsementOfCertificate: "",
  certificateNumber: "",

  amountPayable: "",
  amountPaidOrDeemedToBePaid: "",
  amountDue: "",

  considerationOtherThanCashDetails: "",

  dateOfTransferTransmissionRedemptionForfeiture: "",

  numberOfUnitsTransferredOrTransmittedOrForfeitedRedeemed: "",

  transfereeDistinctiveNumbersFrom: "",
  transfereeDistinctiveNumbersTo: "",

  folioOfTransferee: "",
  nameOfTransferee: "",

  balanceUnitsLeft: "",

  otherRemarks: "",
  authentication: "",
});

const FIELDS = [
  { label: "Allotment / Transfer No.", key: "allotmentOrTransferNo" },

  {
    label: "Date of Allotment / Transfer",
    key: "dateOfAllotmentOrTransfer",
    type: "date",
  },

  {
    label: "No. of Debentures / Securities Allotted / Transferred",
    key: "numberOfDebenturesOrSecuritiesAllottedOrTransferred",
    type: "number",
  },

  {
    label: "Distinctive No. – From",
    key: "allotmentDistinctiveFrom",
    type: "number",
  },

  {
    label: "Distinctive No. – To",
    key: "allotmentDistinctiveTo",
    type: "number",
  },

  { label: "Folio of Transferor", key: "folioOfTransferor" },

  { label: "Name of Transferor", key: "nameOfTransferor" },

  {
    label: "Date of Issue / Endorsement of Certificate",
    key: "dateOfIssueOrEndorsementOfCertificate",
    type: "date",
  },

  { label: "Certificate No.", key: "certificateNumber" },

  { label: "Amount – Payable (₹)", key: "amountPayable", type: "number" },

  { label: "Amount – Paid (₹)", key: "amountPaid", type: "number" },

  { label: "Amount – Due (₹)", key: "amountDue", type: "number" },

  {
    label: "Consideration Other Than Cash",
    key: "considerationOtherThanCashDetails",
  },

  {
    label: "Date of Transfer / Transmission / Redemption / Forfeiture",
    key: "dateOfTransferTransmissionRedemptionForfeiture",
    type: "date",
  },

  {
    label: "No. of Units Transferred / Transmitted / Forfeited / Redeemed",
    key: "numberOfUnitsTransferredOrTransmittedOrForfeitedRedeemed",
    type: "number",
  },

  {
    label: "Transfer Distinctive No. – From",
    key: "transfereeDistinctiveFrom",
    type: "number",
  },

  {
    label: "Transfer Distinctive No. – To",
    key: "transfereeDistinctiveTo",
    type: "number",
  },

  { label: "Folio of Transferee", key: "folioOfTransferee" },

  { label: "Name of Transferee", key: "nameOfTransferee" },

  { label: "Balance Units Left", key: "balanceUnitsLeft", type: "number" },

  { label: "Remarks", key: "otherRemarks" },

  { label: "Authentication / Signature", key: "authentication" },
];

const Mgt2RegisterViewer = ({ selectedClientData }) => {
  const token = useSelector((state) => state.auth.access_token);

  const [registerData, setRegisterData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMgt2Id, setSelectedMgt2Id] = useState(null);
  const [selectedFolioNo, setSelectedFolioNo] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const printRef = useRef(null);
  const accountId = selectedClientData?.accountId || selectedClientData?.id;

  const fetchRegisterData = useCallback(async () => {
    if (!accountId || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await mgt2Api.fetchMgt2Register(accountId, token);
      setRegisterData(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("MGT-2 fetch error:", err);
      setError("Not data available of MGT-2");
    } finally {
      setLoading(false);
    }
  }, [accountId, token]);

  const fetchSummary = useCallback(async () => {
    if (!accountId || !token) return;
    setSummaryLoading(true);
    try {
      const res = await mgt2Api.fetchMgt2ShareSummary(accountId, token);
      setSummaryData(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("Summary fetch error:", err);
    } finally {
      setSummaryLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => {
    fetchRegisterData();
    fetchSummary();
  }, [fetchRegisterData, fetchSummary]);

  const handleSubmit = async () => {
    if (!selectedMgt2Id) return;
    setSubmitting(true);
    setSubmitError(null);

    const payload = {
      allotmentOrTransferNo: form.allotmentOrTransferNo,
      dateOfAllotmentOrTransfer: form.dateOfAllotmentOrTransfer,

      numberOfDebenturesOrSecuritiesAllottedOrTransferred: Number(
        form.numberOfDebenturesOrSecuritiesAllottedOrTransferred,
      ),

      allotmentDistinctiveNumber: {
        distinctiveNumbersFrom: Number(form.distinctiveNumbersFrom),
        distinctiveNumbersTo: Number(form.distinctiveNumbersTo),
      },

      folioOfTransferor: form.folioOfTransferor,
      nameOfTransferor: form.nameOfTransferor,

      dateOfIssueOrEndorsementOfCertificate:
        form.dateOfIssueOrEndorsementOfCertificate,

      certificateNumber: form.certificateNo,

      amount: {
        amountPayable: Number(form.amountPayable),
        amountPaidOrDeemedToBePaid: Number(form.amountPaid),
        amountDue: Number(form.amountDue),
      },

      considerationOtherThanCashDetails: form.considerationOtherThanCashDetails,

      dateOfTransferTransmissionRedemptionForfeiture:
        form.dateOfTransferTransmissionRedemptionForfeiture,

      numberOfUnitsTransferredOrTransmittedOrForfeitedRedeemed: Number(
        form.numberOfUnitsTransferredOrTransmittedOrForfeitedRedeemed,
      ),

      transfereeDistinctiveNumber: {
        transfereeDistinctiveNumbersFrom: Number(
          form.transfereeDistinctiveNumbersFrom,
        ),
        transfereeDistinctiveNumbersTo: Number(
          form.transfereeDistinctiveNumbersTo,
        ),
      },

      folioOfTransferee: form.folioOfTransferee,
      nameOfTransferee: form.nameOfTransferee,

      balanceUnitsLeft: Number(form.balanceUnitsLeft),

      otherRemarks: form.otherRemarks,
      authentication: form.authentication,
    };

    try {
      const res = await mgt2Api.addNewDebentureHolding(
        selectedMgt2Id,
        payload,
        token,
      );
      setModalOpen(false);
      setForm(emptyForm());
      await Promise.all([fetchRegisterData(), fetchSummary()]);
    } catch (err) {
      console.error("Add share-holding error:", err);
      setSubmitError(
        err?.message || "Failed to add share holding. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrintToPDF = async () => {
    if (!printRef.current) return;

    await html2pdf()
      .set({
        margin: [0, 0, 0, 0],
        filename: `MGT1_Register_${selectedClientData?.accountName || "Company"}.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: {
          scale: 3,
          scrollX: 0,
          scrollY: 0,
          useCORS: true,
          logging: false,
          windowWidth: 794, // A4 portrait width in pixels
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: true,
        },
        pagebreak: {
          mode: ["avoid-all", "css"],
        },
      })
      .from(printRef.current)
      .save();
  };

  const fmt = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };
  const cur = (v) =>
    v || v === 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "-";
  const val = (v) =>
    v !== null && v !== undefined && v !== "" ? String(v) : "-";
  const toNum = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);

  const openModal = (member) => {
    setSelectedMgt2Id(member.id);
    setSelectedFolioNo(member.folioNumber);
    setForm(emptyForm());
    setSubmitError(null);
    setModalOpen(true);
  };

  if (!selectedClientData)
    return <p className="text-center text-gray-500 mt-10">Select an account</p>;

  return (
    <div className="bg-gray-100 min-h-screen p-2">
      <div className="flex flex-wrap justify-between items-center mb-0 gap-2">
        <h2 className="font-bold text-lg text-gray-800">
          MGT-2 Register of Debenture Holders
        </h2>
    
      </div>

      <div className="flex border-b border-gray-300 mb-1">
        <button
          onClick={() => setActiveTab("summary")}
          className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${activeTab === "summary" ? "border-b-2 border-blue-600 text-blue-700 bg-white" : "text-gray-500 hover:text-gray-700"}`}
        >
          📊 Debenture holders Summary
        </button>
        <button
          onClick={() => setActiveTab("register")}
          className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${activeTab === "register" ? "border-b-2 border-blue-600 text-blue-700 bg-white" : "text-gray-500 hover:text-gray-700"}`}
        >
          📋 MGT-2 Register
        </button>
      </div>

      {activeTab === "summary" && (
        <DebentureHoldingSummary
          summaryData={summaryData}
          registerData={registerData}
          showAction={true}
          onAddShare={openModal}
        />
      )}

      {activeTab === "register" && (
        <>
        <div className="flex gap-2 mb-3 justify-end items-end">
            <button
              onClick={() => {
                fetchRegisterData();
                fetchSummary();
              }}
              className="bg-primary text-white px-4 py-1.5 rounded text-sm font-semibold"
            >
              🔄 Reload
            </button>

            <button
              onClick={handlePrintToPDF}
              disabled={registerData.length === 0}
              className="bg-primary disabled:opacity-40 text-white px-4 py-1.5 rounded text-sm font-semibold"
            >
              📄 Export PDF
            </button>
          </div>
          {loading ? (
            <p className="text-center text-gray-400 py-10">Loading MGT-1…</p>
          ) : error ? (
            <p className="text-center text-red-600 py-10">{error}</p>
          ) : registerData.length === 0 ? (
            <p className="text-center text-gray-400 py-10">
              No MGT-1 data found
            </p>
          ) : (

            <div ref={printRef}>
              
              {/* SUMMARY PAGE (Portrait) */}
              <div style={PAGE} className="shadow-md mx-auto mb-6">
                <DebentureHoldingSummary
                  summaryData={summaryData}
                  registerData={registerData}
                  showAction={false}
                />
              </div>

              {/* FOLIO PAGES (Portrait, table will auto-break to next page if needed) */}
              {registerData.map((member, i) => {
                const pd = member.personalDetails || {};
                const md = member.minorDetailsHolder || {};
                const ms = md.minorMembershipHolderDetails || {};
                const sd = member.securityDetails || {};
                const addr = member.registeredOfficeAddress || {};
                const debentureHolding =
                  member.debentureAllotmentTransferDetailsList || [];

                return (
                  <div key={i} style={PAGE} className="shadow-md mx-auto mb-8">
                    {/* HEADER */}
                    <div style={{ textAlign: "center", marginBottom: "4px" }}>
                      <div style={{ fontSize: "11px", fontWeight: "bold" }}>
                        Form No. MGT - 2
                      </div>
                      <div style={{ fontSize: "10px", fontWeight: "bold" }}>
                        Register of debenture holders/ other securities holders.
                      </div>
                      <div style={{ fontSize: "9px", marginTop: "1px" }}>
                        [Pursuant to section 88(1)(b) and ( c) of the Companies
                        Act, 2013 and rule 4 of the Companies (Management and
                        Administration) Rules, 2014]
                      </div>
                    </div>

                    {/* COMPANY INFO */}
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "10px",
                        marginBottom: "4px",
                        marginTop: "3px",
                      }}
                    >
                      <tbody>
                        <tr>
                          <td style={{ ...LABEL_CELL, fontWeight: "bold" }}>
                            Name of the Company:
                          </td>
                          <td style={VALUE_CELL}>{val(member.companyName)}</td>
                        </tr>
                        <tr>
                          <td style={LABEL_CELL}>Registered office address:</td>
                          <td style={VALUE_CELL}>
                            {addr.address
                              ? `${addr.address}, ${addr.city || ""}, ${addr.state || ""}, ${addr.pincode || ""}`
                              : "-"}
                          </td>
                        </tr>
                        <tr>
                          <td
                            colSpan={2}
                            style={{
                              fontSize: "7px",
                              fontStyle: "italic",
                              color: "#555",
                            }}
                          >
                            TO BE MAINTAINED SEPARATELY FOR EACH CLASS OF SHARES
                          </td>
                        </tr>
                        <tr>
                          <td style={LABEL_CELL}>Class of shares:</td>
                          <td style={VALUE_CELL}>
                            {val(member.classOfDebentures)}
                          </td>
                        </tr>
                        <tr>
                          <td style={LABEL_CELL}>
                            Nominal value per share (in Rs.):
                          </td>
                          <td style={VALUE_CELL}>
                            {val(member.nominalValuePerUnit)}
                          </td>
                        </tr>
                        <tr>
                          <td style={LABEL_CELL}>
                            Total no. of debentures / other securities held:
                          </td>
                          <td style={VALUE_CELL}>
                            {val(member.totalNumberOfDebentures)}
                          </td>
                        </tr>
                        <tr>
                          <td style={LABEL_CELL}>Total shares held:</td>
                          <td style={VALUE_CELL}>
                            {val(member.totalSharesHeld)}
                          </td>
                        </tr>

                        <tr>
                          <td style={{ ...LABEL_CELL, fontWeight: "bold" }}>
                            FOLIO NO.
                          </td>
                          <td style={{ ...VALUE_CELL, fontWeight: "bold" }}>
                            {val(member.folioNumber)}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {/* PERSONAL + MINOR (2-col) */}
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "4px",
                        fontSize: "10px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "bold",
                            marginBottom: "2px",
                            borderBottom: "1px solid #000",
                            paddingBottom: "3px",
                            fontSize: "12px",
                          }}
                        >
                          Personal details
                        </div>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "10px",
                          }}
                        >
                          <tbody>
                            <PersonalRow
                              label="Name of the member"
                              value={pd.holderName}
                            />
                            <PersonalRow
                              label="Name of joint holders"
                              value={pd.jointHoldersNames}
                            />
                            <PersonalRow label="Address" value={pd.address} />
                            <PersonalRow label="E-mail" value={pd.emailId} />

                            <PersonalRow
                              label="UID No"
                              value={pd.uniqueIdentificationNo}
                            />
                            <PersonalRow
                              label="Father/Mother/Spouse"
                              value={pd.fatherOrMotherOrSpouseName}
                            />

                            <PersonalRow
                              label="Occupation"
                              value={pd.occupation}
                            />
                            <PersonalRow label="PAN" value={pd.panNo} />
                            <PersonalRow
                              label="Nationality"
                              value={pd.nationality}
                            />
                          </tbody>
                        </table>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "bold",
                            marginBottom: "2px",
                            borderBottom: "1px solid #000",
                            paddingBottom: "3px",
                            fontSize: "12px",
                          }}
                        >
                          In case member is a minor,
                        </div>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "10px",
                          }}
                        >
                          <tbody>
                            <PersonalRow
                              label="Guardian"
                              value={md.guardianName}
                            />
                            <PersonalRow
                              label="DOB of minor"
                              value={fmt(md.minorDateOfBirth)}
                            />

                            <>Details of membership</>
                            <PersonalRow
                              label="Date of becoming debenture holder/ other security holder :  "
                              value={fmt(ms.dateOfBecomingHolder)}
                            />
                            <PersonalRow
                              label="Date of receipt of nomination :"
                              value={ms.dateOfReceiptOfNomination}
                            />
                            <PersonalRow
                              label="No. of debentures / securities kept in abeyance, if any :"
                              value={fmt(ms.abeyanceSecuritiesCount)}
                            />
                            <PersonalRow
                              label="Record of lien on debentures / securities , if any :"
                              value={ms.lienRecord}
                            />

                            <PersonalRow
                              label="Date of cessation as debenture holder/ other security holder : "
                              value={ms.dateOfCessation}
                            />
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div
                      style={{
                        gap: "9px",
                        marginBottom: "4px",
                        fontSize: "8px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "bold",
                          marginBottom: "2px",
                          paddingBottom: "1px",
                          fontSize: "12px",
                        }}
                      >
                        Security details
                      </div>
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "10px",
                        }}
                      >
                        <tbody>
                          <PersonalRow
                            label="Whether repayment of the debentures / other securities is secured: Yes / No"
                            value={sd.secured}
                          />
                          <PersonalRow
                            label="If secured, date of registration of charge with the ROC:"
                            value={fmt(md.chargeRegistrationDate)}
                          />
                          <PersonalRow
                            label="Charge Identification Number allotted for the charge :  "
                            value={fmt(ms.chargeIdentificationNumber)}
                          />
                          <PersonalRow
                            label="Brief particulars of the assets to secure such debentures / other securities :"
                            value={ms.assetParticulars}
                          />
                        </tbody>
                      </table>
                    </div>

                    {/* INSTRUCTIONS */}
                    <div
                      style={{
                        marginTop: "4px",
                        marginBottom: "4px",
                        fontSize: "9px",
                      }}
                    >
                      <div style={{ fontWeight: "bold", marginBottom: "1px" }}>
                        Instructions
                      </div>
                      <div>
                        Particulars of dividend mandates, power of attorney and
                        other instructions, if any:
                      </div>
                      <div>Instruction for sending notices etc., if any:</div>
                    </div>

                    {/* SHARE HOLDING TABLE - SPLIT INTO TWO PARTS FOR READABILITY */}
                    <div>
                      <div
                        style={{
                          fontWeight: "bold",
                          marginBottom: "4px",
                          fontSize: "10px",
                        }}
                      >
                        Details of Debenture holding (Part 1 - Allotment
                        Details)
                      </div>

                      {/* PART 1: Columns 1-10 (Allotment section) */}
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "6px",
                          marginBottom: "10px",
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#f0f0f0" }}>
                            <th style={TH}>Allotment No./ Transfer No.</th>
                            <th style={TH}>Date of allotment/</th>
                            <th style={TH}>No. of shares allotted</th>
                            <th style={TH}>Distinctive No From</th>
                            <th style={TH}>Distinctive No To</th>
                            <th style={TH}>Folio of transferor</th>
                            <th style={TH}>Name of transferor</th>
                            <th style={TH}>Date of issue</th>
                            <th style={TH}>Certificate No.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debentureHolding.map((t, idx) => (
                            <tr key={idx}>
                              <td style={TD}>{val(t.allotmentOrTransferNo)}</td>
                              <td style={TD}>
                                {fmt(t.dateOfAllotmentOrTransfer)}
                              </td>
                              <td style={TD}>
                                {val(t.numberOfSharesAllottedOrTransferred)}
                              </td>
                              <td style={TD}>
                                {val(
                                  t.distinctiveNumbersFrom
                                    ?.distinctiveNumbersFrom,
                                )}
                              </td>
                              <td style={TD}>
                                {val(
                                  t.distinctiveNumberFromTo
                                    ?.distinctiveNumbersTo,
                                )}
                              </td>
                              <td style={TD}>{val(t.folioOfTransferor)}</td>
                              <td style={TD}>{val(t.nameOfTransferor)}</td>
                              <td style={TD}>
                                {fmt(t.dateOfIssueOrEndorsementOfCertificate)}
                              </td>
                              <td style={TD}>{val(t.certificateNumber)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* PART 2: Columns 11-23 (Payment & Transfer section) */}
                      <div
                        style={{
                          fontWeight: "bold",
                          marginBottom: "4px",
                          fontSize: "10px",
                        }}
                      >
                        Details of debenture holding (Part 2 - Payment &
                        Transfer Details)
                      </div>

                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          fontSize: "10px",
                        }}
                      >
                        <thead>
                          <tr style={{ background: "#f0f0f0" }}>
                            <th style={TH}>Ammount Payable (₹)</th>
                            <th style={TH}>Amount Paid/ Deemed (₹)</th>
                            <th style={TH}>Amount Due (₹)</th>
                            <th style={TH}>Consideration other than cash</th>
                            <th style={TH}>Date of transfer</th>
                            <th style={TH}>No. shares transferred</th>
                            <th style={TH}>Transfer No From</th>
                            <th style={TH}>Transfer No To</th>
                            <th style={TH}>Folio of transferee</th>
                            <th style={TH}>Name of transferee</th>
                            <th style={TH}>Balance shares</th>
                            <th style={TH}>Remarks</th>
                            <th style={TH}>Auth/ Signature</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debentureHolding.map((t, idx) => (
                            <tr key={idx}>
                              <td style={TD}>{cur(t.amount?.amountPayable)}</td>
                              <td style={TD}>
                                {cur(t.amount?.amountPaidOrDeemedToBePaid)}
                              </td>
                              <td style={TD}>{cur(t.amount?.amountDue)}</td>
                              <td style={TD}>
                                {val(t.considerationOtherThanCashDetails)}
                              </td>
                              <td style={TD}>
                                {fmt(
                                  t.dateOfTransferTransmissionRedemptionForfeiture,
                                )}
                              </td>
                              <td style={TD}>
                                {val(t.numberOfSharesAllottedOrTransferred)}
                              </td>
                              <td style={TD}>
                                {val(
                                  t.transfereeDistinctiveNumbersFrom
                                    ?.transfereeDistinctiveNumbersFrom,
                                )}
                              </td>
                              <td style={TD}>
                                {val(
                                  t.transfereeDistinctiveNumbersTo
                                    ?.transfereeDistinctiveNumbersTo,
                                )}
                              </td>
                              <td style={TD}>{val(t.folioOfTransferee)}</td>
                              <td style={TD}>{val(t.nameOfTransferee)}</td>
                              <td style={TD}>{val(t.balanceUnitsLeft)}</td>
                              <td style={TD}>{val(t.otherRemarks)}</td>
                              <td style={TD}>{val(t.authentication)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50"
          style={{ paddingTop: "40px" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <div className="mt-3 px-5  flex justify-between items-center rounded-t-xl ">
              <div>
                <h3 className="font-bold text-base text-gray-600">+ Add New Debenture Holding</h3>
                <p className="text-gray-600 text-xs">
                  Folio No: <strong>{selectedFolioNo}</strong>
                </p>
              </div>
              <button
                onClick={() => setModalOpen(false)}
                className="text-red-700 hover:text-red-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
             <hr className=" border-gray-800" />
            <div className="p-3">
              {submitError && (
                <div className="bg-red-50 border border-red-300 text-red-700 text-xs rounded px-3 py-2 mb-4">
                  ⚠️ {submitError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-5 gap-y-3">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-0.5">
                      {f.label}
                    </label>
                    <input
                      type={f.type || "text"}
                      value={form[f.key]}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          [f.key]: e.target.value,
                        }))
                      }
                      disabled={submitting}
                      className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-100"
                      placeholder={
                        f.type === "date"
                          ? "yyyy-mm-dd"
                          : f.type === "number"
                            ? "0"
                            : "—"
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-5 py-2 bg-primary disabled:opacity-50 text-white text-sm font-semibold rounded flex items-center gap-2"
                >
                  {submitting && (
                    <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {submitting ? "Saving…" : "💾 Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PersonalRow = ({ label, value }) => (
  <tr>
    <td
      style={{
        paddingRight: "4px",
        whiteSpace: "nowrap",
        verticalAlign: "top",
        width: "48%",
        fontSize: "6.5px",
      }}
    >
      {label}
    </td>
    <td style={{ verticalAlign: "top", fontSize: "6.5px" }}>
      {value !== null && value !== undefined && value !== ""
        ? String(value)
        : "-"}
    </td>
  </tr>
);

export default Mgt2RegisterViewer;
